import React, { useEffect } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";

import styled from "styled-components";

import Alert from "components/atoms/alert/Alert";
import Loading from "components/atoms/loading/Loading";
import Toast from "components/atoms/toast/Toast";

import AccountPage from "components/page/AccountPage";
import ErrorPage from "components/page/ErrorPage";
import LandingPage from "components/page/LandingPage";
import LoginPage from "components/page/LoginPage";
import PasswordResetPage from "components/page/PasswordResetPage";
import PlanPage from "components/page/PlanPage";
import PrivacyPolicyPage from "components/page/PrivacyPolicyPage";
import ReferenceDetailPage from "components/page/ReferenceDetailPage";
import ReferenceListPage from "components/page/ReferenceListPage";
import ReferenceReviewPage from "components/page/ReferenceReviewPage";
import ResearchDetailPage from "components/page/ResearchDetailPage";
import ResearchListPage from "components/page/ResearchListPage";
import SearchPage from "components/page/SearchPage";
import SettingsPage from "components/page/SettingsPage";
import SignUpPage from "components/page/SignUpPage";
import SpacePage from "components/page/SpacePage";
import TermsOfServicePage from "components/page/TermsOfServicePage";

import { useUser } from "hooks/queries/useUser";
import { useSessionPageHistory } from "hooks/useSessionPageHistory";

import GoogleAnalyticsService from "services/GoogleAnalyticsService";

import "App.scss";

const Container = styled.div`
    width: 100%;
    height: 100%;
    min-height: inherit;
    ${(props) =>
        props.review &&
        `
    overflow: hidden;
    `}
`;

const Row = styled.div`
    display: flex;
    align-items: flex-start;
`;

const isValidUuid = (value) => {
    if (typeof value !== "string") {
        return false;
    }
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
};

function App() {
    const location = useLocation();
    const pathname = location.pathname;

    useSessionPageHistory();
    GoogleAnalyticsService();

    const ProtectedRoute = ({ children }) => {
        if (localStorage.getItem("token")) {
            return <Outlet />;
        } else {
            Alert("warn", "에러", "로그인이 필요합니다.");
            return <Navigate to={`/login?next=${encodeURIComponent(`${pathname}${location.search || ""}`)}`} replace />;
        }
    };

    const SpaceRoute = () => {
        const { space_uuid } = useParams();
        const navigate = useNavigate();
        const userQuery = useUser();

        useEffect(() => {
            if (userQuery.data) {
                const currentSpace = userQuery.data.current_space;
                const privateSpace = userQuery.data.private_space;

                if (!currentSpace) {
                    navigate(`/${privateSpace.uuid}/reference`, { replace: true });
                }
            }
        }, [userQuery.data, navigate, space_uuid]);

        return <Outlet />;
    };

    return (
        <div className="App">
            <Loading>
                <Toast />
                <Container review={pathname.includes("/review")}>
                    <Row>
                        <Routes>
                            <Route path="/" element={<LandingPage />} />

                            <Route exact path={"/plan"} element={<PlanPage />} />
                            <Route exact path={"/privacy-policy"} element={<PrivacyPolicyPage />} />
                            <Route exact path={"/terms-of-service"} element={<TermsOfServicePage />} />

                            <Route exact path={"/login"} element={<LoginPage />} />
                            <Route exact path={"/signup"} element={<SignUpPage />} />
                            <Route exact path={"/reset-password"} element={<PasswordResetPage />} />

                            <Route exact path={"/error"} element={<ErrorPage />} />

                            {/* 로그인 필요 페이지 */}
                            <Route element={<ProtectedRoute />}>
                                {/* 스페이스와 무관한 페이지 */}
                                <Route exact path={"/account"} element={<AccountPage />} />
                                <Route exact path={"/settings"} element={<SettingsPage />} />

                                {/* 스페이스 하위 페이지 */}
                                <Route path={":space_uuid"} element={<SpaceRoute />}>
                                    <Route index element={<Navigate to="reference" replace />} />

                                    <Route path={"search"} element={<SearchPage />} />
                                    <Route path={"reference"}>
                                        <Route index element={<ReferenceListPage />} />
                                        <Route path={":reference_uuid"} element={<ReferenceDetailPage />} />
                                        <Route
                                            path={":reference_uuid/review/:reference_file_uuid"}
                                            element={<ReferenceReviewPage />}
                                        />
                                    </Route>

                                    <Route path={"research"}>
                                        <Route index element={<ResearchListPage />} />
                                        <Route path={":uuid"} element={<ResearchDetailPage />} />
                                    </Route>
                                    <Route path={"management"} element={<SpacePage />} />
                                </Route>
                            </Route>
                        </Routes>
                    </Row>
                </Container>
            </Loading>
        </div>
    );
}

export default App;
